import {
  repositoryDelivery,
  repositoryMdm,
  repositoryPos,
} from "../../../../_base/_const/_constVar";
import type { MessageResponse } from "../../../../model/MessageResponse";
import type { SelectListItem } from "../../../../model/SelectListItem";
import type { ReturnSourceType } from "../config/returnKinds";
import type {
  CreateReturnInwardRequest,
  PositionOption,
  ReturnInwardHeader,
  ReturnOrderInfo,
  ReturnOrderLine,
  ReturnSerialRow,
  WarehouseOption,
} from "../types";

const BASE = "/api/v1/Inward";
const enc = encodeURIComponent;

/** Dữ liệu phiếu backend trả (mẫu tạo, sửa, xem, in cùng một dạng) */
export interface ReturnInwardDetail {
  tblInventoryTransactionModel: ReturnOrderInfo & ReturnInwardHeader;
  inventoryTransactionRequestSourceModels: ReturnOrderLine[] | null;
  inventoryTransactionSerialModels: ReturnSerialRow[] | null;
}

/** Mẫu tạo phiếu theo đơn đổi trả, đồng thời lấy mã phiếu NK mới (API cũ, không đổi) */
export const getCreateTemplate = (sourceCode: string, sourceType: ReturnSourceType, branchCode?: string | null) =>
  repositoryPos.get<MessageResponse<ReturnInwardDetail>>(
    `${BASE}/create-in-ward-return?prefix=NK&SourceCode=${enc(sourceCode)}&SourceType=${enc(sourceType)}${
      branchCode ? `&BranchCode=${enc(branchCode)}` : ""
    }`,
  );

/**
 * Chi tiết phiếu để sửa hoặc xem. Backend chỉ nhận id (trang cũ gửi transactionCode nên báo
 * "Giao dịch không được tìm thấy!").
 */
export const getInwardDetail = (mode: "edit" | "detail", id: number | string) =>
  repositoryPos.get<MessageResponse<ReturnInwardDetail>>(`${BASE}/${mode}-in-ward-return?id=${enc(String(id))}`);

export interface SearchSerialBody {
  serial: string;
  sourceCode: string;
  type: string;
  /** Hàng không quản lý serial: bỏ kiểm tra số lượng tồn của serial */
  isNoSerial: boolean | null;
}

/** Kiểm tra và lấy thông tin một serial khách trả (serial phải thuộc đơn đổi trả) */
export const searchSerial = (body: SearchSerialBody) =>
  repositoryPos.post<MessageResponse<ReturnSerialRow>>("api/v1/TblItemTransaction/search-serial-item-transaction", {
    ...body,
    invId: null,
    branchId: 0,
  });

export interface SaveResult {
  ok: boolean;
  /** false: backend báo lỗi HTTP, lớp gọi API đã hiện thông báo */
  responded: boolean;
  /** Id phiếu backend trả về */
  transactionId: number;
}

/**
 * Lưu tạm (OPEN) hoặc hoàn thành (LOCK) phiếu qua API mới.
 * Backend báo lỗi bằng HTTP 500; lớp gọi API đã hiện thông báo của backend và trả undefined.
 */
export const saveReturnInward = async (request: CreateReturnInwardRequest): Promise<SaveResult> => {
  const response = await repositoryPos.post<MessageResponse<any>>(`${BASE}/create-return-in-ward`, request);
  // Controller bọc MessageResponse của handler (handler không đặt success): id phiếu nằm ở data.data
  const ok = response?.httpStatusCode === 200 && !!response?.success;
  const inner = response?.data;
  const id = inner && typeof inner === "object" ? inner.data : inner;
  return { ok, responded: !!response, transactionId: ok ? Number(id) || 0 : 0 };
};

/** Kho chọn được của chi nhánh, lọc mã như trang cũ */
export const getReturnWarehouses = async (branchId: number, codeParts: string[]): Promise<WarehouseOption[]> => {
  const res = await repositoryPos.get<MessageResponse<WarehouseOption[]>>(
    `/api/v1/TblDmInventory/get-select-by-branch?branchId=${enc(String(branchId))}`,
  );
  if (!res?.success || !Array.isArray(res.data)) return [];
  return res.data.filter((inv) => codeParts.some((part) => String(inv.inventoryCode ?? "").includes(part)));
};

/** Vị trí của kho */
export const getPositions = async (invId: number): Promise<PositionOption[]> => {
  const res = await repositoryDelivery.get<MessageResponse<any[]>>(
    `api/v1/TblDmItemPosition/get-select-by-subInvId?subInvId=${enc(String(invId))}`,
  );
  if (!res?.success || !Array.isArray(res.data)) return [];
  return res.data
    .filter((p) => p?.id != null)
    .map((p) => ({
      id: Number(p.id),
      code: String(p.positionCode ?? ""),
      name: String(p.positionName ?? ""),
      label: `${p.positionCode ?? ""} - ${p.positionName ?? ""}`,
    }));
};

/** Tên các tình trạng serial đang dùng (status Y), như trang cũ */
export const getSerialStatuses = async (): Promise<string[]> => {
  const res = await repositoryPos.get<MessageResponse<any[]>>("/api/v1/TblSerialStatus/get-list?Skip=0&Take=100");
  if (!res?.success || !Array.isArray(res.data)) return [];
  return res.data.filter((i) => i?.code && i?.name && i?.status === "Y").map((i) => String(i.name));
};

export interface MasterOrg {
  orgId: number;
  orgCode: string;
}

/** Mã chi nhánh để hiện "mã · tên chi nhánh" */
export const getMasterOrgs = async (): Promise<MasterOrg[]> => {
  const res = await repositoryDelivery.get<MessageResponse<any[]>>("/api/v1/TblDmMasterOrg/get-all");
  if (!res?.success || !Array.isArray(res.data)) return [];
  return res.data.map((i: any) => ({ orgId: i.id, orgCode: i.code }));
};

/** Dữ liệu in phiếu nhập đổi trả (chỉ phiếu đã khoá) */
export const getPrintData = (id: number, withServices: boolean) =>
  repositoryPos.get<MessageResponse<ReturnInwardDetail>>(
    `${BASE}/print-in-ward-return?id=${enc(String(id))}${withServices ? "&isserive=true" : ""}`,
  );

/** Tỉnh / phường xã mới theo id xã */
export const getCommunes = (communeId: string) =>
  repositoryMdm.get<MessageResponse<SelectListItem[]>>(`/api/v1/TblDmCommuneMoi/get-select?Ids=${enc(communeId)}`);
